import { useCallback } from 'react';
import { prefillFromInventory, type TradeInventoryItem } from '@/services/trades/trade-service';
import { marketDataService } from '@/services/market-data/market-data-service';
import { SLBPosition } from '@/types/inventory.types';
import type { TradeForm } from '../../components/trade-form-sections/types';
import type { SearchItem } from '../useCreateTradeForm';
import { snapshotGrossPrice } from './useInventoryPrefillEffects';

type ValidationError = { field: string; message: string; severity: 'error' | 'warning' };

/**
 * LR-495: the security-dropdown selection handlers, extracted verbatim from
 * useCreateTradeForm.
 *
 * `handleSelectSecurity` writes settlement_venue (and ~15 other fields) from the
 * inventory prefill; `useInventoryDefaults` later writes settlement_venue again
 * and WINS by design — that double-write order is preserved because this
 * extraction changes neither the hook-call ordering nor when the effect fires.
 * The LR-378 TickDB market-price lookup keeps its stale-response guard.
 */
export function useSecuritySelectionHandlers({
  form,
  allSLBPositions,
  benchmarkRates,
  setSelectedInventoryItem,
  setSecuritySearchQuery,
  setShowSecurityDropdown,
  setHasFormChanges,
  setValidationErrors,
}: {
  form: TradeForm;
  allSLBPositions: SLBPosition[];
  benchmarkRates: Record<string, number>;
  setSelectedInventoryItem: (item: SearchItem['item'] | null) => void;
  setSecuritySearchQuery: (query: string) => void;
  setShowSecurityDropdown: (show: boolean) => void;
  setHasFormChanges: (changed: boolean) => void;
  setValidationErrors: (errors: ValidationError[]) => void;
}): {
  // Explicit return type: TradeForm is derived from ReturnType<typeof
  // useCreateTradeForm>, and these handlers feed back into that return — an
  // inferred return type here would make the parent's return type circular.
  handleSelectSecurity: (selectedItem: SearchItem) => void;
  handleClearSecurity: () => void;
} {
  const handleSelectSecurity = useCallback((selectedItem: SearchItem) => {
    const { item, groupedItem } = selectedItem;
    const firstCounterparty = groupedItem.counterparties[0];
    if (firstCounterparty) {
      const counterpartyPosition = allSLBPositions.find(p => p.counterparty === firstCounterparty.name && p.symbolId === groupedItem.symbol_id);
      if (counterpartyPosition) {
        item.legalEntity = counterpartyPosition.legalEntity;
        item.accountId = counterpartyPosition.accountId;
        item.counterparty = counterpartyPosition.counterparty;
      }
    }
    setSelectedInventoryItem(item);
    setHasFormChanges(true);
    setShowSecurityDropdown(false);
    const displayText = item.shortName && item.shortName !== item.symbolId
      ? `${item.shortName} (${item.symbolId})`
      : item.symbolId;
    setSecuritySearchQuery(displayText);
    const tradeItem: TradeInventoryItem = {
      legalEntity: item.legalEntity,
      accountId: item.accountId,
      counterparty: item.counterparty,
      symbolId: item.symbolId,
      shortName: item.shortName,
      sedol: item.sedol,
      isin: item.isin,
      type: item.type,
      price: item.price,
      quantity: item.quantity,
      currency: item.currency,
      leverageRate: 0,
      benchmarkFee: item.benchmarkFee,
    };
    const prefilled = prefillFromInventory(tradeItem, benchmarkRates);
    if (prefilled.security?.symbol) {
      form.setFieldValue('symbol_id', prefilled.security.symbol);
    }
    if (prefilled.security?.isin) {
      form.setFieldValue('isin', prefilled.security.isin);
    } else if (item.isin) {
      form.setFieldValue('isin', item.isin);
    }
    if (prefilled.security?.description) {
      form.setFieldValue('description', prefilled.security.description);
    } else if (item.shortName) {
      form.setFieldValue('description', item.shortName);
    }
    if (prefilled.security?.currency) {
      form.setFieldValue('currency', prefilled.security.currency);
    } else if (item.currency) {
      form.setFieldValue('currency', item.currency);
    }
    if (prefilled.account?.legalEntity) {
      form.setFieldValue('booking_center', prefilled.account.legalEntity);
    } else if (item.legalEntity) {
      form.setFieldValue('booking_center', item.legalEntity);
    }
    if (prefilled.position?.contractPrice) {
      form.setFieldValue('gross_price', snapshotGrossPrice(prefilled.position.contractPrice));
    }
    if (prefilled.position?.tradeType) {
      form.setFieldValue('trade_type', prefilled.position.tradeType);
    }
    if (prefilled.rate?.fixedRate) {
      form.setFieldValue('benchmark_rate', prefilled.rate.fixedRate.toString());
    }
    if (prefilled.settlement?.venue) {
      form.setFieldValue('settlement_venue', prefilled.settlement.venue);
    }
    if (prefilled.settlement?.deliveryType) {
      form.setFieldValue('delivery_type', prefilled.settlement.deliveryType);
    }
    // fx_rate belongs to the pair (see useSelectedItemForTradePrefill).
    if (prefilled.settlement?.settlementCurrency) {
      form.setFieldValue('settlement_currency', prefilled.settlement.settlementCurrency);
      form.setFieldValue('fx_rate', prefilled.settlement.fxRate);
    }
    if (prefilled.rate?.dividendPassThrough) {
      form.setFieldValue('dividend_passthrough_percent', prefilled.rate.dividendPassThrough.toString());
    }
    // trade_time travels with trade_date (see useSelectedItemForTradePrefill).
    if (prefilled.dates?.tradeDate && prefilled.dates.tradeTime) {
      form.setFieldValue('trade_date', prefilled.dates.tradeDate);
      form.setFieldValue('trade_time', prefilled.dates.tradeTime);
    }
    if (prefilled.dates?.settlementDate) {
      form.setFieldValue('settlement_date', prefilled.dates.settlementDate);
    }
    if (prefilled.dates?.tradeDuration) {
      form.setFieldValue('trade_duration', prefilled.dates.tradeDuration);
    }
    if (prefilled.dates?.noticePeriodDays !== undefined) {
      form.setFieldValue('notice_period_days', prefilled.dates.noticePeriodDays);
    }
    // LR-378: fire-and-forget TickDB lookup to auto-fill Current Market Price.
    // Silent on failure (no toast) — operator can always enter manually.
    // Stale-response guard: if the operator switches to a different security
    // before the fetch resolves, we drop the result so the form doesn't
    // overwrite the newer prefill with the older price.
    const targetSymbolId = item.symbolId;
    marketDataService.fetchQuote(targetSymbolId).then((quote) => {
      if (!quote) return;
      if (form.state.values.symbol_id !== targetSymbolId) return;
      form.setFieldValue('current_market_price', quote.price.toFixed(2));
    });
    // Setters are referentially stable useState dispatchers, so listing them keeps
    // the callback identity unchanged vs the original (where deps were just
    // [allSLBPositions, benchmarkRates, form]); they're here only to satisfy
    // exhaustive-deps now that they arrive as props, not local useState handles.
  }, [allSLBPositions, benchmarkRates, form, setSelectedInventoryItem, setHasFormChanges, setShowSecurityDropdown, setSecuritySearchQuery]);

  const handleClearSecurity = useCallback(() => {
    setSelectedInventoryItem(null);
    setSecuritySearchQuery('');
    setShowSecurityDropdown(false);
    setHasFormChanges(true);
    // Drop the previous security's validation results so a quick clear→reselect
    // within the 300ms validation debounce can't briefly surface stale errors
    // (e.g. an "exceeds available" with the old quantity) for the new security.
    setValidationErrors([]);
    form.setFieldValue('symbol_id', '');
  }, [form, setSelectedInventoryItem, setSecuritySearchQuery, setShowSecurityDropdown, setHasFormChanges, setValidationErrors]);

  return { handleSelectSecurity, handleClearSecurity };
}
